import { ChartFacade } from '@mode/charts/contract';
import { ReportViewerFacade } from '@mode/report-viewer/domain';
import { DataViewFacade } from '@mode/switch-views/contract';
import type { StateService } from '@uirouter/angularjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { isPresent } from '@mode/shared/util-js';
import { ExplorerSelectors } from '~/selectors/explorer.selectors';
import { AppSelect } from '~/types/main.types';
import { ReportViewContainerObservables } from '~/types/observable-creators.types';
/**
 * Make the observables object for the reportViewContainer.
 *
 * Should not use SelectedReportState
 */
export function makeReportViewContainerObservables(
  reportViewerFacade: ReportViewerFacade,
  $state: StateService,
  store: AppSelect,
  chartFacade: ChartFacade,
  dataViewFacade: DataViewFacade
): ReportViewContainerObservables {
  const showFilterHeader$ = reportViewerFacade.showFilterHeader$;
  const showParamsHeader$ = reportViewerFacade.showParamsHeader$;
  const hasRun$ = reportViewerFacade.hasRun$;
  const webLink$ = reportViewerFacade.webLink$;
  const reportRunDetailsLink$ = reportViewerFacade.reportRunDetailsLink$;
  const reportRunErrorLink$ = reportViewerFacade.reportRunErrorLink$;
  const mostRecentReportRunLink$ = reportViewerFacade.mostRecentReportRunLink$;
  const hasRecentRun$ = reportViewerFacade.hasRecentReportRun$;
  const reportHasFailuresSinceLastSuccess$ = reportViewerFacade.reportHasFailuresSinceLastSuccess$;
  const lastSuccessfullyRunAt$ = reportViewerFacade.lastSuccessfullyRunAt$;
  const reportRunCompletedAt$ = reportViewerFacade.reportRunCompletedAt$;
  const newerReportRunLink$ = reportViewerFacade.mostRecentReportRunToken$.pipe(
    map((mostRecentRunToken) => $state.href('reportShow.runs', { runId: mostRecentRunToken }, { absolute: true }))
  );

  const rowStyle$ = reportViewerFacade.reportRunFormFields$.pipe(
    map((fields) => {
      const totalFormFields = fields.length;
      const maxRows = 3;
      return `rows-${Math.min(Math.ceil(totalFormFields / 4), maxRows)}`;
    })
  );

  const reportRunBannerType$ = reportViewerFacade.reportRunBannerType$;

  const token$ = store.select(ExplorerSelectors.getToken);

  const switchViewToken$ = token$.pipe(
    switchMap((token) => chartFacade.createSwitchViewTokenFromVizToken(token).pipe(filter(isPresent)))
  );

  const isLocal$ = switchViewToken$.pipe(
    switchMap((token) =>
      dataViewFacade
        .createEnhancedDatasetResultBySwitchViewToken(token)
        .pipe(map((datasetResult) => datasetResult.isLocal))
    )
  );

  return {
    showFilterHeader$,
    showParamsHeader$,
    webLink$,
    hasRun$,
    hasRecentRun$,
    lastSuccessfullyRunAt$,
    reportRunCompletedAt$,
    reportRunDetailsLink$,
    reportRunErrorLink$,
    reportHasFailuresSinceLastSuccess$,
    newerReportRunLink$,
    rowStyle$,
    reportRunBannerType$,
    mostRecentReportRunLink$,
    isLocal$,
  };
}
